// Small inline icon set (24px grid, 1.75 stroke). Decorative by default (aria-hidden).

import type { CSSProperties } from "react";

export type IconName =
  | "check"
  | "alert"
  | "stop"
  | "question"
  | "plus"
  | "x"
  | "chevron-down"
  | "chevron-up"
  | "chevron-right"
  | "chevron-left"
  | "copy"
  | "link"
  | "download"
  | "upload"
  | "qr"
  | "chat"
  | "register"
  | "overview"
  | "survey"
  | "book"
  | "file"
  | "logout"
  | "menu"
  | "search"
  | "refresh"
  | "trash"
  | "external"
  | "send"
  | "building"
  | "mail"
  | "pencil"
  | "clock"
  | "catalog"
  | "shield"
  | "lightbulb"
  | "nodes";

const paths: Record<IconName, JSX.Element> = {
  check: <path d="M5 12.5l4.3 4.3L19 7.2" />,
  alert: (
    <>
      <path d="M12 6.5v7.2" />
      <circle cx="12" cy="17.6" r="0.6" fill="currentColor" />
    </>
  ),
  stop: <path d="M6.5 12h11" />,
  question: (
    <>
      <path d="M9.4 9.3a2.7 2.7 0 1 1 3.9 2.4c-.8.4-1.3 1-1.3 1.9v.6" />
      <circle cx="12" cy="17.4" r="0.6" fill="currentColor" />
    </>
  ),
  plus: <path d="M12 5.5v13M5.5 12h13" />,
  x: <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />,
  "chevron-down": <path d="M6.5 9.5L12 15l5.5-5.5" />,
  "chevron-up": <path d="M6.5 14.5L12 9l5.5 5.5" />,
  "chevron-right": <path d="M9.5 6.5L15 12l-5.5 5.5" />,
  "chevron-left": <path d="M14.5 6.5L9 12l5.5 5.5" />,
  copy: (
    <>
      <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
      <path d="M15.5 8.5V6.5a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h2" />
    </>
  ),
  link: (
    <>
      <path d="M10.5 13.5a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 0 0-5-5l-.8.8" />
      <path d="M13.5 10.5a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 0 0 5 5l.8-.8" />
    </>
  ),
  download: (
    <>
      <path d="M12 4.5v10.5M7.5 10.5L12 15l4.5-4.5" />
      <path d="M5 19.5h14" />
    </>
  ),
  upload: (
    <>
      <path d="M12 15V4.5M7.5 9L12 4.5 16.5 9" />
      <path d="M5 19.5h14" />
    </>
  ),
  qr: (
    <>
      <rect x="4.5" y="4.5" width="6" height="6" rx="1" />
      <rect x="13.5" y="4.5" width="6" height="6" rx="1" />
      <rect x="4.5" y="13.5" width="6" height="6" rx="1" />
      <path d="M13.5 13.5h2.5v2.5M19.5 13.5v.01M13.5 19.5h.01M16.5 19.5h3v-3" />
    </>
  ),
  chat: <path d="M5 6.5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v7.5a2 2 0 0 1-2 2h-6.2L7 19.5v-3.5a2 2 0 0 1-2-2z" />,
  register: (
    <>
      <path d="M9 7h10M9 12h10M9 17h10" />
      <circle cx="5.2" cy="7" r="1" fill="currentColor" />
      <circle cx="5.2" cy="12" r="1" fill="currentColor" />
      <circle cx="5.2" cy="17" r="1" fill="currentColor" />
    </>
  ),
  overview: (
    <>
      <rect x="4.5" y="4.5" width="6.5" height="6.5" rx="1.5" />
      <rect x="13" y="4.5" width="6.5" height="6.5" rx="1.5" />
      <rect x="4.5" y="13" width="6.5" height="6.5" rx="1.5" />
      <rect x="13" y="13" width="6.5" height="6.5" rx="1.5" />
    </>
  ),
  survey: (
    <>
      <circle cx="9" cy="8.5" r="3" />
      <path d="M3.8 19c.6-3 2.7-4.6 5.2-4.6s4.6 1.6 5.2 4.6" />
      <circle cx="17" cy="9.5" r="2.3" />
      <path d="M16 14.5c2.3-.2 4 1.2 4.4 4" />
    </>
  ),
  book: (
    <>
      <path d="M12 6.8c-1.8-1.4-4.4-2-7.5-1.8v13c3.1-.2 5.7.4 7.5 1.8 1.8-1.4 4.4-2 7.5-1.8V5c-3.1-.2-5.7.4-7.5 1.8z" />
      <path d="M12 6.8v13" />
    </>
  ),
  file: (
    <>
      <path d="M7 3.5h7l4.5 4.5v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-15.5a1 1 0 0 1 1-1z" />
      <path d="M13.5 3.5V8.5h5" />
    </>
  ),
  logout: (
    <>
      <path d="M14 5.5H7a1.5 1.5 0 0 0-1.5 1.5v10A1.5 1.5 0 0 0 7 18.5h7" />
      <path d="M11 12h9M17 8.5l3.5 3.5-3.5 3.5" />
    </>
  ),
  menu: <path d="M4.5 7h15M4.5 12h15M4.5 17h15" />,
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="M15.5 15.5L20 20" />
    </>
  ),
  refresh: (
    <>
      <path d="M19 12a7 7 0 1 1-2.1-5" />
      <path d="M19.3 4.5v3.3H16" />
    </>
  ),
  trash: (
    <>
      <path d="M5 7h14M10 4.5h4M7 7l.8 12a1.5 1.5 0 0 0 1.5 1.4h5.4a1.5 1.5 0 0 0 1.5-1.4L17 7" />
    </>
  ),
  external: (
    <>
      <path d="M13.5 4.5h6v6M19.5 4.5L11 13" />
      <path d="M17 14v4a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 5 18V8.5A1.5 1.5 0 0 1 6.5 7h4" />
    </>
  ),
  send: <path d="M5 12l14-7-5.5 14-2.5-5.5z" />,
  building: (
    <>
      <path d="M5 20V5.5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1V20M15 10h3a1 1 0 0 1 1 1v9M3.5 20h17" />
      <path d="M8.5 8.5h3M8.5 12h3M8.5 15.5h3" />
    </>
  ),
  mail: (
    <>
      <rect x="4" y="6" width="16" height="12" rx="2" />
      <path d="M4.5 7l7.5 6 7.5-6" />
    </>
  ),
  pencil: <path d="M5 19l1-4 9.5-9.5a2 2 0 0 1 3 3L9 18z M13.5 7.5l3 3" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="7.5" />
      <path d="M12 8v4.3l2.8 1.7" />
    </>
  ),
  catalog: (
    <>
      <rect x="4.5" y="4.5" width="15" height="15" rx="2" />
      <path d="M4.5 9.5h15M9.5 9.5v10" />
    </>
  ),
  shield: <path d="M12 4l7 2.5v5c0 4.4-3 7.6-7 8.8-4-1.2-7-4.4-7-8.8v-5z" />,
  lightbulb: (
    <>
      <path d="M9 17.5h6M10 20.5h4" />
      <path d="M8.2 14.2A6 6 0 1 1 15.8 14.2c-.8.7-1.3 1.6-1.3 2.6v.7h-5v-.7c0-1-.5-1.9-1.3-2.6z" />
    </>
  ),
  nodes: (
    <>
      <path d="M12 12L12 5.2M12 12l5.9-3.4M12 12l5.9 3.4M12 12v6.8M12 12l-5.9 3.4M12 12L6.1 8.6" strokeWidth="1.3" />
      <circle cx="12" cy="12" r="2.1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="4.6" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="18.4" cy="8.3" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="18.4" cy="15.7" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19.4" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="5.6" cy="15.7" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="5.6" cy="8.3" r="1.6" fill="currentColor" stroke="none" />
    </>
  ),
};

export function Icon({
  name,
  size = 18,
  className,
  style,
  title,
  strokeWidth = 1.75,
}: {
  name: IconName;
  size?: number;
  className?: string;
  style?: CSSProperties;
  title?: string;
  strokeWidth?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      aria-label={title}
      focusable="false"
    >
      {paths[name]}
    </svg>
  );
}
